import { FunctionComponent, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ShareIcon from '@mui/icons-material/Share';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CircularProgress from '@mui/material/CircularProgress';
import { useSavedItems } from '../../../contexts/SavedItemsContext';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { EstimatedCosts } from '../../provider';
import SaveToModal from '../../modals/SaveToModal/SaveToModal';
import CreateSavedListModal from '../../modals/CreateSavedListModal/CreateSavedListModal';
import { procedureService } from '../../../api';
import { useApiCall } from '../../../hooks/useApiCall';
import styles from './ProcedureDetail.module.css';

interface Procedure {
  id: string;
  cpt: string;
  name: string;
  category: string;
  description: string;
  duration: string;
  anesthesia: string;
  recovery: string;
  preparation: string[];
  steps: string[];
  risks: string[];
  benefits: string[];
  alternatives: {
    id: string;
    name: string;
    cpt: string;
    price: number;
  }[];
  providers: {
    id: string;
    name: string;
    specialty: string;
    distance: string;
    rating: number;
    nextAvailable: string;
    price: number;
  }[];
  pricing: {
    facilityFee: {
      withInsurance: number;
      withoutInsurance: number;
    };
    physicianFee: {
      withInsurance: number;
      withoutInsurance: number;
    };
    anesthesiaFee: {
      withInsurance: number;
      withoutInsurance: number;
    };
    total: {
      withInsurance: number;
      withoutInsurance: number;
      savings: number;
    };
  };
  followUp: {
    visits: number;
    duration: string;
    instructions: string[];
  };
  successRate: string;
  complications: string;
}

const ProcedureDetail: FunctionComponent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isItemSaved } = useSavedItems();
  const [procedure, setProcedure] = useState<Procedure | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'costs' | 'providers'>('overview');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  
  const { loading, error, execute: fetchProcedure } = useApiCall(() =>
    procedureService.getProcedureDetail(id || '')
  );

  useEffect(() => {
    const loadProcedure = async () => {
      if (id) {
        const result = await fetchProcedure();
        if (result?.success && result.data) {
          setProcedure(result.data);
        }
      }
    };

    loadProcedure();
  }, [id]);

  const isSaved = procedure ? isItemSaved(procedure.id, 'procedure') : false;

  const handleSave = () => {
    if (!procedure) return;
    setShowSaveModal(true);
  };

  const handleShare = async () => {
    if (navigator.share && procedure) {
      try {
        await navigator.share({
          title: procedure.name,
          text: `Check out ${procedure.name} on Mario Health`,
          url: window.location.href
        });
      } catch (error) {
        // User cancelled the share dialog or an error occurred
        // Silently handle the error - no need to show anything to the user
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else if (!navigator.share) {
      // Fallback for browsers that don't support native share
      console.log('Web Share API not supported');
      // Could implement a custom share modal here
    }
  };

  const handleProviderClick = (provider: any) => {
    navigate(`/provider/${provider.id}`);
  };

  if (loading) {
    return (
      <div className={styles.procedureDetail}>
        <div className={styles.loading}>
          <CircularProgress size={40} />
          <p style={{ marginTop: '16px' }}>Loading procedure details...</p>
        </div>
      </div>
    );
  }

  if (error || !procedure) {
    return (
      <div className={styles.procedureDetail}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </button>
          <h1 className={styles.headerTitle}>Procedure Details</h1>
        </div>
        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
          <p>{error ? 'Unable to load procedure details.' : 'Procedure not found.'}</p>
          <button 
            onClick={() => navigate(-1)} 
            style={{ marginTop: '16px', padding: '8px 16px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const estimatedCosts = [
    {
      label: 'Facility Fee',
      billedAmount: `$${procedure.pricing.facilityFee.withoutInsurance}`,
      insurancePays: `$${procedure.pricing.facilityFee.withoutInsurance - procedure.pricing.facilityFee.withInsurance}`,
      patientCost: `$${procedure.pricing.facilityFee.withInsurance}`,
      savingsPercentage: 70
    },
    {
      label: 'Physician Fee',
      billedAmount: `$${procedure.pricing.physicianFee.withoutInsurance}`,
      insurancePays: `$${procedure.pricing.physicianFee.withoutInsurance - procedure.pricing.physicianFee.withInsurance}`,
      patientCost: `$${procedure.pricing.physicianFee.withInsurance}`,
      savingsPercentage: 70
    },
    {
      label: 'Anesthesia Fee',
      billedAmount: `$${procedure.pricing.anesthesiaFee.withoutInsurance}`,
      insurancePays: `$${procedure.pricing.anesthesiaFee.withoutInsurance - procedure.pricing.anesthesiaFee.withInsurance}`,
      patientCost: `$${procedure.pricing.anesthesiaFee.withInsurance}`,
      savingsPercentage: 67
    },
    {
      label: 'Total Estimated Cost',
      billedAmount: `$${procedure.pricing.total.withoutInsurance}`,
      insurancePays: `$${procedure.pricing.total.withoutInsurance - procedure.pricing.total.withInsurance}`,
      patientCost: `$${procedure.pricing.total.withInsurance}`,
      savingsPercentage: procedure.pricing.total.savings,
      isTotal: true
    }
  ];

  return (
    <div className={styles.procedureDetail}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </button>
        <div className={styles.headerActions}>
          <button className={styles.actionButton} onClick={handleSave}>
            {isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
          </button>
          <button className={styles.actionButton} onClick={handleShare}>
            <ShareIcon />
          </button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.procedureHeader}>
          <MedicalServicesIcon className={styles.procedureIcon} />
          <div className={styles.procedureInfo}>
            <h1 className={styles.procedureName}>{procedure.name}</h1>
            <p className={styles.procedureCategory}>{procedure.category}</p>
            <p className={styles.cptCode}>CPT Code: {procedure.cpt}</p>
          </div>
        </div>

        <div className={styles.quickInfo}>
          <div className={styles.infoItem}>
            <AccessTimeIcon className={styles.infoIcon} />
            <div>
              <span className={styles.infoLabel}>Duration</span>
              <span className={styles.infoValue}>{procedure.duration}</span>
            </div>
          </div>
          <div className={styles.infoItem}>
            <LocalHospitalIcon className={styles.infoIcon} />
            <div>
              <span className={styles.infoLabel}>Anesthesia</span>
              <span className={styles.infoValue}>{procedure.anesthesia}</span>
            </div>
          </div>
          <div className={styles.infoItem}>
            <CheckCircleOutlineIcon className={styles.infoIcon} />
            <div>
              <span className={styles.infoLabel}>Success Rate</span>
              <span className={styles.infoValue}>{procedure.successRate}</span>
            </div>
          </div>
        </div>

        <div className={styles.tabs}>
          <button 
            className={`${styles.tab} ${activeTab === 'overview' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'costs' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('costs')}
          >
            Costs
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'providers' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('providers')}
          >
            Providers
          </button>
        </div>

        {activeTab === 'overview' && (
          <>
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>About This Procedure</h2>
              <p className={styles.description}>{procedure.description}</p>
            </div>

            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>How to Prepare</h2>
              <ul className={styles.prepList}>
                {procedure.preparation.map((prep, index) => (
                  <li key={index}>{prep}</li>
                ))}
              </ul>
            </div>

            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>What to Expect</h2>
              <div className={styles.steps}>
                {procedure.steps.map((step, index) => (
                  <div key={index} className={styles.step}>
                    <div className={styles.stepNumber}>{index + 1}</div>
                    <p className={styles.stepText}>{step}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Benefits & Risks</h2>
              <div className={styles.benefitsRisks}>
                <div className={styles.benefits}>
                  <h3 className={styles.subTitle}>Benefits</h3>
                  <ul className={styles.benefitsList}>
                    {procedure.benefits.map((benefit, index) => (
                      <li key={index}>{benefit}</li>
                    ))}
                  </ul>
                </div>
                <div className={styles.risks}>
                  <h3 className={styles.subTitle}>Potential Risks</h3>
                  <ul className={styles.risksList}>
                    {procedure.risks.map((risk, index) => (
                      <li key={index}>{risk}</li>
                    ))}
                  </ul>
                  <p className={styles.complicationRate}>
                    Complication rate: {procedure.complications}
                  </p>
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Recovery & Follow-up</h2>
              <div className={styles.recoveryInfo}>
                <div className={styles.recoveryItem}>
                  <span className={styles.recoveryLabel}>Recovery Time:</span>
                  <span className={styles.recoveryValue}>{procedure.recovery}</span>
                </div>
                <div className={styles.recoveryItem}>
                  <span className={styles.recoveryLabel}>Follow-up Visits:</span>
                  <span className={styles.recoveryValue}>{procedure.followUp.visits} visit {procedure.followUp.duration}</span>
                </div>
              </div>
              <h3 className={styles.subTitle}>Post-Procedure Instructions</h3>
              <ul className={styles.instructionsList}>
                {procedure.followUp.instructions.map((instruction, index) => (
                  <li key={index}>{instruction}</li>
                ))}
              </ul>
            </div>

            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Alternative Procedures</h2>
              <div className={styles.alternativesList}>
                {procedure.alternatives.map((alt) => (
                  <div 
                    key={alt.id} 
                    className={styles.alternativeCard}
                    onClick={() => navigate(`/procedure/${alt.id}`)}
                  >
                    <MedicalServicesIcon className={styles.altIcon} />
                    <div className={styles.altInfo}>
                      <h4>{alt.name}</h4>
                      <span className={styles.altCode}>CPT: {alt.cpt}</span>
                    </div>
                    <span className={styles.altPrice}>From ${alt.price}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'costs' && (
          <div className={styles.section}>
            <EstimatedCosts
              insurancePlan="Your Insurance"
              costs={estimatedCosts}
              dataSourceNote="Estimates based on your insurance coverage and average costs in your area"
            />
            
            <div className={styles.costNote}>
              <InfoOutlinedIcon className={styles.noteIcon} />
              <p>
                These are estimates only. Final costs may vary based on your specific insurance plan, 
                deductible status, and any additional services required during the procedure.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'providers' && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Available Providers</h2>
            <div className={styles.providerList}>
              {procedure.providers.map((provider, index) => (
                <div 
                  key={index} 
                  className={styles.providerCard}
                  onClick={() => handleProviderClick(provider)}
                >
                  <div className={styles.providerHeader}>
                    <LocalHospitalIcon className={styles.providerIcon} />
                    <div className={styles.providerInfo}>
                      <h3 className={styles.providerName}>{provider.name}</h3>
                      <p className={styles.providerSpecialty}>{provider.specialty}</p>
                      <div className={styles.providerMeta}>
                        <div className={styles.metaItem}>
                          <LocationOnIcon className={styles.smallIcon} />
                          <span>{provider.distance}</span>
                        </div>
                        <div className={styles.metaItem}>
                          <span className={styles.rating}>★ {provider.rating}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.providerDetails}>
                    <div className={styles.availability}>
                      <AccessTimeIcon className={styles.smallIcon} />
                      <span>Next: {new Date(provider.nextAvailable).toLocaleDateString()}</span>
                    </div>
                    <div className={styles.providerPrice}>
                      <span className={styles.priceLabel}>Est. Total</span>
                      <span className={styles.priceValue}>${provider.price}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={styles.bottomActions}>
        <button className={styles.primaryButton}>
          <LocalHospitalIcon />
          Schedule Procedure
        </button>
        <button className={styles.secondaryButton}>
          <LocationOnIcon />
          Compare Locations
        </button>
      </div>

      {procedure && (
        <SaveToModal
          isOpen={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          item={{
            id: procedure.id,
            type: 'procedure',
            title: procedure.name,
            subtitle: `${procedure.category} • CPT: ${procedure.cpt}`,
            data: procedure
          }}
          onCreateNewList={() => {
            setShowSaveModal(false);
            setShowCreateListModal(true);
          }}
        />
      )}

      <CreateSavedListModal
        isOpen={showCreateListModal}
        onClose={() => setShowCreateListModal(false)}
        onListCreated={(listId) => {
          navigate(`/list/${listId}`);
        }}
      />
    </div>
  );
};

export default ProcedureDetail;